import { snakeCase, toLower } from "lodash";
import {
  Avatar,
  BadgeProps,
  ChipState,
  ComplexListItemVariant,
  NestedComplexListItemProps,
  NestedDataCardProps,
  Tooltip,
  createDataCardInitialColumnDefinition,
  createInfoCardColumnDefinition,
  SingleStatusChip,
  OrganizationMembership,
  MembershipStatus,
  MembershipType,
  Typography,
} from "@inductiveautomation/ws-components";

import i18n from "../../i18n/i18n";
import { OrgNodeTree } from "./api/organizations.api";
import { OrganizationActionCallback } from "./Organizations.types";
import { actionsForMembership } from "./Organizations.actions";
import { testIdForOrganization } from "./Organizations.locators";

export const stateForMembershipStatus: Record<string, ChipState> = {
  AVAILABLE: "neutral",
  PENDING: "neutral",
  INVITED: "info",
  JOINED: "neutral",
};

const tooltipForMembershipStatus = (
  status: OrganizationMembership["membershipStatus"]
) => {
  if (status === "PENDING")
    return i18n.t("organizations.status.pending.tooltip");
  if (status === "INVITED")
    return i18n.t("organizations.status.invited.tooltip");
  if (status === "JOINED") return i18n.t("organizations.status.joined.tooltip");
  return i18n.t("organizations.status.available.tooltip");
};

export const getStatusesFromMemberships = (
  data: OrganizationMembership[]
): MembershipStatus[] => {
  return [
    ...new Set(
      data.map((membership): MembershipStatus => {
        return getStatusFromMembership(membership);
      })
    ),
  ];
};

export const getStatusFromMembership = (
  membership: OrganizationMembership
): MembershipStatus => {
  return membership.membershipStatus;
};

export const getMembershipTypesFromMemberships = (
  data: OrganizationMembership[]
): MembershipType[] => {
  return [
    ...new Set(
      data.map((membership): MembershipType => membership.membershipType)
    ),
  ] as MembershipType[];
};

export const getEmailsFromMemberships = (
  data: OrganizationMembership[]
): string[] => {
  return [
    ...new Set(data.map((membership): string => membership.membershipEmail)),
  ];
};

const getStatus = (membership: OrganizationMembership) => {
  return i18n.t(
    `organizations.status.${toLower(getStatusFromMembership(membership))}.label`
  );
};

export const organizationVerifiedBadgeProps = {
  size: "small" as BadgeProps["size"],
  custom: "verified" as const,
};

export const membershipKind = (membership: OrganizationMembership) => {
  if (
    ["PENDING", "INVITED", "AVAILABLE"].includes(membership.membershipStatus)
  ) {
    return "-";
  }
  if (membership.membershipType === "MEMBER") {
    return i18n.t("organizations.membership.member.label");
  }
  return i18n.t("organizations.membership.guest.label");
};

export const organizationToDataCard =
  (onAction: OrganizationActionCallback) =>
  (membership: OrganizationMembership): NestedDataCardProps => {
    const testId = testIdForOrganization(membership.organization);
    const { orgName, orgPath } = membership.organization;
    return {
      id: membership.organization.orgId,
      testId,
      variant: "checkbox",
      hasDivider: true,
      rootName: orgPath[0] ?? orgName,
      contentColumns: [
        createDataCardInitialColumnDefinition({
          parentTestId: testId,
          heading: orgName,
          subtext: orgPath.length > 1 ? orgPath.join(" › ") : "",
          avatar: (
            <Avatar
              icon="organization"
              iconAvatarColor="primary"
              topBadgeProps={
                membership.organization.isVerified
                  ? organizationVerifiedBadgeProps
                  : undefined
              }
            />
          ),
        }),
        {
          ...createInfoCardColumnDefinition(
            <Typography variant="body2">{membershipKind(membership)}</Typography>
          ),
          alignment: "left",
          maxWidth: undefined,
          minWidth: 200,
        },
        {
          ...createInfoCardColumnDefinition(
            <Typography
              variant="body2"
              component="span"
              sx={{
                minWidth: "1px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {membership.membershipEmail}
            </Typography>
          ),
          alignment: "left",
          maxWidth: undefined,
          minWidth: 200,
        },
        {
          ...createInfoCardColumnDefinition(
            <Tooltip
              testId={`${testId}-info-tooltip`}
              title={tooltipForMembershipStatus(membership.membershipStatus)}
            >
              <SingleStatusChip
                testId={`${testId}-info-chip`}
                state={stateForMembershipStatus[membership.membershipStatus]}
                label={getStatus(membership)}
              />
            </Tooltip>
          ),
          maxWidth: 160,
        },
      ],
      actionLists: [actionsForMembership(membership, onAction)],
      childItems: [],
    };
  };

/**
 * Options for decorating org-tree list items with a "direct member" status chip.
 * Used by the leave flow to flag descendant orgs the current account is a direct
 * member of — access to those is unaffected, so they are tagged rather than hidden.
 */
export interface OrgNodeTreeListItemOptions {
  // Org ids that should render the `directMemberChip`.
  directMemberOrgIds?: Set<string>;
  // Chip node rendered on matching entries (built by the caller so i18n stays in components).
  directMemberChip?: React.ReactNode;
}

export const orgNodeTreeToListItem = (
  organization: OrgNodeTree,
  testIdPrefix: string,
  variant?: ComplexListItemVariant,
  options?: OrgNodeTreeListItemOptions
): NestedComplexListItemProps => ({
  id: organization.orgId,
  testId: `${testIdPrefix}-${snakeCase(organization.name)}`,
  heading: organization.name,
  subtext: organization.address,
  variant: variant ?? "radio",
  density: "dialog",
  // Rendered in the far-right `info` slot (not `chip`, which sits beside the heading)
  // so the status is right-aligned.
  info: options?.directMemberOrgIds?.has(organization.orgId)
    ? options.directMemberChip
    : undefined,
  avatar: (
    <Avatar
      icon="organization"
      iconAvatarColor="primary"
      topBadgeProps={
        organization.isVerified ? organizationVerifiedBadgeProps : undefined
      }
    />
  ),
  childItems: organization.children.map((child) =>
    orgNodeTreeToListItem(child, testIdPrefix, variant, options)
  ),
});
